import { type FormEvent, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router";
import { MODELS, MUTATIONS, useModel, useMutation } from "../../../api";
import type { ProjectFormField, WorkshopStepSpark } from "../../../api/spark";
import type { ShowroomEntry } from "../../../api/showroom";
import type { SmallTeamId } from "../../../api/smallTeam";
import { ROUTES, SPARK_ROUTES } from "../../../routes.js";
import cx from "../../../lib/cx.js";
import { uploadFile } from "../../../lib/uploader.jsx";
import { uploadVideo } from "../../../lib/videoUploadHandler.jsx";
import { triggerConfetti } from "../../../design-system/utils/Celebration/confetti.jsx";
import { setFeedbackTrigger } from "../../feedback/feedbackTriggerStore";
import { specialFields } from "../showroom/ShowroomEntryParts";
import { ShowroomEntryCard } from "../showroom/ShowroomEntryPreview";
import type { IconName } from "../../../design-system/primitives/icons";
import PageShell from "../../../design-system/components/PageShell/PageShell";
import Button from "../../../design-system/primitives/components/Button/Button";
import Text from "../../../design-system/primitives/components/Text/Text";
import ErrorAlert from "../../../design-system/primitives/components/ErrorAlert/ErrorAlert";
import ProgressIndicator from "../../../design-system/primitives/components/ProgressIndicator/ProgressIndicator";
import { type UploadValue, useObjectUrl } from "./FileUpload";
import {
  type ShowroomFormBag,
  type Values,
  FormField,
  isEmpty,
  validate,
} from "./fields";
import styles from "./showroomForm.css";

// ---- Step copy ----

interface StepCopy {
  title: string;
  subtitle?: string;
  /**
   * What the step's input asks. The step's title is the field's label, so the
   * field shows none of its own and the placeholder does the inviting.
   */
  placeholder?: string;
}

/**
 * The wizard's headings. The create-workshop wizard keys these by step number
 * in its own config, which the showroom form can't: its steps come from the
 * spark's Sanity form definition, so both their number and their order vary.
 * Almost every spark ships exactly the four fields below, in this order — those
 * get the copy here. A spark with extra or differently worded fields (kujg's
 * "Forderung", iba-fortbildung's Sie-form) falls back to the label and
 * description Sanity already carries for the field, so its own voice survives.
 *
 * TODO: where does the copy live? A lot of copy is defined within sanity's
 * project_form_definition, some here.
 */
const fieldCopy: Record<string, StepCopy> = {
  title: {
    title: "Gebt eurem Projekt einen Titel",
    subtitle: "Unter diesem Titel erscheint euer Projekt im Showroom.",
    placeholder: "Wie heißt euer Projekt?",
  },
  coverImage: {
    title: "Ladet ein Coverbild hoch",
    subtitle: "Das Coverbild macht euer Projekt im Showroom sichtbar.",
  },
  description: {
    title: "Beschreibt euer Projekt",
    subtitle: "Worum geht es? Was habt ihr gemacht, was habt ihr gelernt?",
    placeholder: "Projektbeschreibung",
  },
  upload: {
    title: "Ladet euer Projekt hoch",
    subtitle: "Als Datei oder als Link – teilt euer Ergebnis.",
  },
};

// The step after the last field: nothing left to fill in, only the entry as
// the showroom will show it, and the button that puts it there.
const finishCopy: StepCopy = {
  title: "Fast geschafft!",
  subtitle: "So erscheint euer Projekt im Showroom.",
};

const getStepCopy = (field: ProjectFormField | undefined): StepCopy => {
  if (!field) return finishCopy;
  return (
    fieldCopy[field.key] ?? {
      title: field.label,
      subtitle: field.description || undefined,
    }
  );
};

// ---- Values ----

/**
 * The backend wraps every non-special field's stored value as
 * `{ data, label, type }` for display; the form wants the bare value back.
 */
const unwrap = (raw: unknown): unknown =>
  raw && typeof raw === "object" && "label" in raw && "data" in raw
    ? raw.data
    : raw;

const getInitialValues = (
  fields: ProjectFormField[],
  entry: ShowroomEntry | null,
): Values => {
  const values: Values = {};
  for (const field of fields) {
    const raw = unwrap(entry?.formData[field.key]);
    if (field.type === "image" || field.type === "projectUpload") {
      // The stored shape is the form's own from an earlier save.
      values[field.key] = (raw as UploadValue | undefined) ?? null;
    } else {
      values[field.key] = typeof raw === "string" ? raw : "";
    }
  }
  return values;
};

const omit = <T,>(record: Record<string, T>, key: string) => {
  const { [key]: _dropped, ...rest } = record;
  return rest;
};

// ---- The preview ----

/**
 * The form's values in the shape the showroom serves an entry, so the card
 * reads them the way it reads a stored one: the fields it renders itself stay
 * bare, the rest carry the label and type it shows them under.
 */
const toPreviewFormData = (fields: ProjectFormField[], values: Values) =>
  Object.fromEntries(
    fields.map((field) => [
      field.key,
      specialFields.includes(field.key)
        ? values[field.key]
        : { data: values[field.key], label: field.label, type: field.type },
    ]),
  );

/**
 * The picture the card leads with: the cover image, or the project upload when
 * that is an image itself - the two the showroom's own card falls through. The
 * wizard only ever runs on a new entry, so these are files the browser still
 * holds rather than anything with a path.
 */
const getCoverFile = (values: Values): File | null => {
  const cover = values.coverImage;
  if (cover instanceof File) return cover;
  const upload = values.upload;
  return upload instanceof File && upload.type.startsWith("image")
    ? upload
    : null;
};

const SAVE_FAILED_MESSAGE =
  "Speichern hat nicht geklappt. Versucht es noch einmal.";

// ---- The form ----

const Titles = ({ title, subtitle }: StepCopy) => (
  <div className={styles.titles}>
    <Text variant="h4">{title}</Text>
    {subtitle && <Text variant="body">{subtitle}</Text>}
  </div>
);

/**
 * The team's own showroom entry, as the wizard that creates it or the page
 * that edits it. Route component for `/team-diary/:smallTeamId`.
 *
 * Owns every piece of state and hands the fields what they need as a `bag`.
 * Errors are not state: each field's rule error is derived from its value on
 * render, and only shown once the field has been touched. The one stored
 * kind of error is a failed upload, which arrives after the entry has saved.
 */
const ShowroomForm = ({ spark }: { spark: WorkshopStepSpark }) => {
  // All three are in the route's path, so none of them is ever missing.
  const { smallTeamId, workshopSlug, sparkSlug } = useParams() as {
    smallTeamId: SmallTeamId;
    workshopSlug: string;
    sparkSlug: string;
  };
  const smallTeam = useModel(MODELS.SMALL_TEAM({ smallTeamId }));
  const teamMembers = useModel(MODELS.SMALL_TEAM_MEMBERS(smallTeamId));
  const { entry } = useModel(MODELS.SMALL_TEAM_SHOWROOM_ENTRY(smallTeam.id));
  const [doSetEntry] = useMutation(MUTATIONS.setShowroomEntry);
  const navigate = useNavigate();
  const sparkUrl = ROUTES.spark.getUrl({ workshopSlug, sparkSlug });
  const showroomUrl = SPARK_ROUTES.showroom({ workshopSlug, sparkSlug });

  const fields = spark.projectFormDefinition?.fields ?? [];
  // Decided once: saving revalidates the entry, and the wizard that just saved
  // must not turn into the edit page while it shows its success.
  const [mode] = useState<ShowroomFormBag["mode"]>(entry ? "edit" : "create");
  const [values, setValues] = useState(() => getInitialValues(fields, entry));
  const [touched, setTouched] = useState<Record<string, true>>({});
  const [uploadErrors, setUploadErrors] = useState<Record<string, string>>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [step, setStep] = useState(0);
  const [submitState, setSubmitState] = useState<"idle" | "pending" | "done">(
    "idle",
  );
  const [videoProgress, setVideoProgress] = useState(0);
  const navigateTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);
  const videoTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    return () => {
      // Reading `.current` at cleanup time is the point here: these hold whatever
      // timeout was pending when the component unmounted. The rule's usual advice —
      // copy the ref into a local inside the effect — would capture `null`, since the
      // effect runs once on mount and the timeouts are set later.
      /* oxlint-disable react-hooks/exhaustive-deps */
      if (navigateTimeout.current) clearTimeout(navigateTimeout.current);
      if (videoTimeout.current) clearTimeout(videoTimeout.current);
      /* oxlint-enable react-hooks/exhaustive-deps */
    };
  }, []);

  const ruleErrors: Record<string, string | null> = {};
  const errors: ShowroomFormBag["errors"] = {};
  for (const field of fields) {
    const ruleError = validate(field, values[field.key]);
    ruleErrors[field.key] = ruleError;
    errors[field.key] =
      uploadErrors[field.key] ??
      (touched[field.key] ? ruleError : undefined) ??
      undefined;
  }
  const isValid = fields.every((field) => !ruleErrors[field.key]);
  const canSubmit = isValid && submitState === "idle";
  const pending = submitState === "pending";

  const bag: ShowroomFormBag = {
    mode,
    values,
    errors,
    setValue: (key, value) => {
      setValues((current) => ({ ...current, [key]: value }));
      setTouched((current) => ({ ...current, [key]: true }));
      setUploadErrors((current) => omit(current, key));
    },
    resetValue: (key) => {
      setValues((current) => ({ ...current, [key]: null }));
      setTouched((current) => omit(current, key));
      setUploadErrors((current) => omit(current, key));
    },
  };

  /**
   * Uploads every newly picked file, then saves the entry with what came back.
   * A file that fails to upload does not stop the save - the entry is stored
   * without it and the field shows what went wrong.
   */
  const submit = async () => {
    setSubmitState("pending");
    setFormError(null);
    const formData: Record<string, unknown> = {};
    const failed: Record<string, string> = {};
    // A plain `File` is a newly picked one, anything else is the stored info
    // of a file that is already there. Saving an edit — or an entry with no new
    // file — is not the `afterUpload` moment, and a form is only ever asked once.
    const hasNewUpload = fields.some(
      (field) => values[field.key] instanceof File,
    );

    try {
      await Promise.all(
        fields.map(async (field) => {
          const value = values[field.key];
          // Empty values are not stored, required or not.
          if (isEmpty(value)) return;
          if (!(value instanceof File)) {
            formData[field.key] = value;
            return;
          }
          const onError = (error: { message: string }) => {
            failed[field.key] = error.message;
          };
          if (value.type.startsWith("video")) {
            const { token, channelId } = await MUTATIONS.createPeertubeToken();
            await uploadVideo({
              file: value,
              key: field.key,
              token,
              channelId: String(channelId),
              onProgress: setVideoProgress,
              onUploaded: (result: { video?: unknown }) => {
                if (result.video) {
                  formData[field.key] = { type: "video", data: result.video };
                  setVideoProgress(0);
                }
              },
              onError,
              timeoutRef: videoTimeout,
            });
          } else {
            await uploadFile({
              key: field.key,
              category: "showroom-uploads",
              file: value,
              // The upload handler hands the file back along with the result;
              // only the result is stored.
              onUploaded: ({ file: _file, ...result }: { file: File }) => {
                formData[field.key] = { type: "file", data: result };
              },
              onError,
            });
          }
        }),
      );
      await doSetEntry({
        smallTeamId: smallTeam.id,
        workshopId: smallTeam.workshopId,
        formData,
      });
    } catch {
      setFormError(SAVE_FAILED_MESSAGE);
      setSubmitState("idle");
      return;
    }

    const failedKeys = Object.keys(failed);
    if (failedKeys.length > 0) {
      setSubmitState("idle");
      setUploadErrors(
        Object.fromEntries(
          failedKeys.map((key) => [
            key,
            `Dein Projekt wurde gespeichert, aber mit dieser Datei ist etwas schiefgelaufen: ${failed[key]}`,
          ]),
        ),
      );
      setStep(fields.findIndex((field) => field.key === failedKeys[0]));
      return;
    }

    setSubmitState("done");
    triggerConfetti();
    // Announced here rather than on the showroom we navigate to: this is the
    // upload succeeding, and the store outlives this component.
    if (hasNewUpload) setFeedbackTrigger("afterUpload");
    // Land where the entry just went, not back on the spark hub - both
    // uploading and saving end with the project on display.
    navigateTimeout.current = setTimeout(() => {
      void navigate(showroomUrl);
    }, 2000);
  };

  const coverSrc = useObjectUrl(getCoverFile(values));

  const stepField: ProjectFormField | undefined = fields[step];
  const stepCopy = getStepCopy(stepField);
  const isFinalStep = stepField === undefined;

  const onSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (mode === "edit" || isFinalStep) {
      if (canSubmit) void submit();
    } else {
      setStep(step + 1);
    }
  };

  const goBack = () => {
    if (step === 0) {
      void navigate(sparkUrl);
    } else {
      setStep(step - 1);
    }
  };

  const busyLabel =
    videoProgress > 0
      ? `Wird hochgeladen... (${videoProgress}%)`
      : "Wird hochgeladen...";

  // The last step's upload button differs from the others: it sits under the
  // preview rather than in the footer, and spans it.
  const submitButton = (
    title: string,
    icon: IconName | undefined,
    {
      color = "accent",
      fullWidth = false,
    }: { color?: "accent" | "success"; fullWidth?: boolean } = {},
  ) => (
    <Button
      title={pending ? busyLabel : title}
      color={submitState === "done" ? "success" : color}
      size="large"
      iconRight={submitState === "done" ? "check" : icon}
      type="submit"
      fullWidth={fullWidth}
      disabled={
        mode === "edit" || isFinalStep
          ? !canSubmit
          : Boolean(ruleErrors[stepField.key])
      }
    />
  );

  // Editing an entry is a single page of every field rather than a wizard, so
  // it gets the shell without a progress indicator and with one action: save.
  if (mode === "edit") {
    return (
      <form noValidate onSubmit={onSubmit} className={styles.form}>
        <PageShell
          surface="dark"
          titleButton={{
            icon: "arrow-left",
            ariaLabel: "Zurück",
            to: sparkUrl,
          }}
          titleSlot={
            <Titles
              title="Bearbeitet euer Projekt"
              subtitle="So erscheint es im Showroom, sobald ihr speichert."
            />
          }
          bottomSlot={
            <footer className={styles.editFooter}>
              {submitButton("Speichern", undefined)}
            </footer>
          }
        >
          <div className={cx(styles.editFields, pending && styles.disabled)}>
            {fields.map((field) => (
              <FormField key={field.key} field={field} bag={bag} />
            ))}
            {formError && <ErrorAlert title={formError} />}
          </div>
        </PageShell>
      </form>
    );
  } else {
    return (
      // The form wraps the shell rather than sitting inside it: the submit button
      // lives in the bottom slot, below the sheet, and has to stay in the same
      // form as the step's inputs.
      <form noValidate onSubmit={onSubmit} className={styles.form}>
        <PageShell
          surface="dark"
          titleSlot={<Titles {...stepCopy} />}
          bottomSlot={
            <footer className={styles.footer}>
              <Button
                title="Zurück"
                color="accent"
                size="large"
                iconLeft="arrow-left"
                type="button"
                onClick={goBack}
              />
              <div className={styles.progress}>
                <ProgressIndicator
                  totalSteps={fields.length + 1}
                  currentStep={step + 1}
                  ariaLabel="Fortschritt beim Hochladen des Projekts"
                />
              </div>
              {isFinalStep ? (
                // The upload button sits under the preview on the last step,
                // and this holds its place in the footer.
                <div className={styles.footerSpacer} />
              ) : (
                submitButton("Weiter", "arrow-right")
              )}
            </footer>
          }
        >
          <div className={cx(styles.step, pending && styles.disabled)}>
            {stepField ? (
              <FormField
                key={stepField.key}
                field={stepField}
                bag={bag}
                placeholder={stepCopy.placeholder}
              />
            ) : (
              <>
                <ShowroomEntryCard
                  formData={toPreviewFormData(fields, values)}
                  memberNames={teamMembers.map((member) => member.name)}
                  coverSrc={coverSrc}
                />
                {submitButton("Hochladen", "upload", {
                  color: "success",
                  fullWidth: true,
                })}
              </>
            )}
            {formError && <ErrorAlert title={formError} />}
          </div>
        </PageShell>
      </form>
    );
  }
};

export default ShowroomForm;
